import { useMemo } from 'react'
import UpgradeTrack from 'app/frontend/types/custom/UpgradeTrack'
import Season from 'app/frontend/types/custom/Season'
import Card from 'app/frontend/base/components/Card'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { armorTypeIcons } from 'app/frontend/types/custom/ArmorType'
import { itemSlotLabels } from 'app/frontend/types/custom/ItemSlot'
import {
  ArmorType,
  armorTypeOrder,
  isAutoObtained,
  MemberRow,
  TierSlot,
  tierSlots,
} from './tierPriorityHelpers'

type Props = {
  rows: MemberRow[]
  allRows: MemberRow[]
  minimumTrack: UpgradeTrack
  selectedSeason: Season
}

const TierPrioritiesSummary = ({
  rows,
  allRows,
  minimumTrack,
  selectedSeason,
}: Props) => {
  const tierEncounters = useMemo(() => {
    const encounters = selectedSeason.instances.flatMap(
      (instance) => instance.encounters,
    )
    return tierSlots
      .map((slot) => {
        const encounter = encounters.find(
          (candidate) => candidate.tierSlotDrop === slot,
        )
        return encounter ? { slot, encounter } : undefined
      })
      .filter(
        (entry): entry is { slot: TierSlot; encounter: wowaudit.Encounter } =>
          !!entry,
      )
  }, [selectedSeason])

  const completionBuckets = useMemo(() => {
    const full = rows.filter((row) => row.obtained >= 4).length
    const partial = rows.filter(
      (row) => row.obtained >= 2 && row.obtained < 4,
    ).length
    const none = rows.filter((row) => row.obtained < 2).length
    const total = rows.length || 1
    return {
      full,
      partial,
      none,
      fullPct: (full / total) * 100,
      partialPct: (partial / total) * 100,
    }
  }, [rows])

  const preferredMatrix = useMemo(() => {
    return tierSlots.map((slot) => {
      const byArmor = Object.fromEntries(
        armorTypeOrder.map((armorType) => [
          armorType,
          allRows.filter((row) => {
            if (row.armorType !== armorType) return false
            if (isAutoObtained(slot, row.tierItems, minimumTrack)) return false
            return row.storedPriority[slot] === 'preferred'
          }).length,
        ]),
      ) as Record<ArmorType, number>

      return {
        slot,
        byArmor,
      }
    })
  }, [allRows, minimumTrack])

  return (
    <aside className='flex flex-col gap-3 w-full min-w-0 lg:w-76 lg:shrink-0'>
      <Card className='space-y-3 py-3 px-4'>
        <div className='flex items-center justify-between pb-2 border-b'>
          <span className='text-base font-medium'>Set completion</span>
          <span className='text-base font-medium'>
            {Math.round(completionBuckets.fullPct)}%
          </span>
        </div>
        <div className='h-3 w-full rounded-full bg-muted overflow-hidden flex'>
          <div
            className='h-full bg-success-foreground'
            style={{ width: `${completionBuckets.fullPct}%` }}
          />
          <div
            className='h-full bg-warning-foreground'
            style={{ width: `${completionBuckets.partialPct}%` }}
          />
        </div>
        <div className='flex items-center gap-4 pt-1 flex-wrap'>
          <CompletionOrb
            label='4 / 5'
            count={completionBuckets.full}
            className='bg-success-foreground'
          />
          <CompletionOrb
            label='2 / 5'
            count={completionBuckets.partial}
            className='bg-warning-foreground'
          />
          <CompletionOrb
            count={completionBuckets.none}
            className='bg-destructive-foreground'
          />
        </div>
      </Card>

      <Card className='space-y-2 py-3 px-4 min-w-0 overflow-hidden'>
        <div className='flex items-center justify-between pb-2 border-b'>
          <span className='text-base font-medium'>Preferred pieces</span>
          <span className='text-sm text-muted-foreground'>by token</span>
        </div>
        <div className='space-y-0'>
          <div className='grid grid-cols-[minmax(0,1fr)_repeat(4,2rem)] items-center text-muted-foreground text-sm'>
            <div className='font-medium'>Slot</div>
            {armorTypeOrder.map((armorType, index) => (
              <div
                key={armorType}
                className={cn(
                  'flex justify-center py-1 rounded-t',
                  index % 2 === 0 && 'bg-muted/60',
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className='inline-flex justify-center'>
                      <BlizzardIcon
                        iconName={armorTypeIcons[armorType]}
                        size='medium'
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{armorType}</TooltipContent>
                </Tooltip>
              </div>
            ))}
          </div>
          {preferredMatrix.map((row, rowIndex) => {
            const encounterName = tierEncounters.find(
              (entry) => entry.slot === row.slot,
            )?.encounter.name
            const isLastRow = rowIndex === preferredMatrix.length - 1
            return (
              <div
                key={row.slot}
                className='grid grid-cols-[minmax(0,1fr)_repeat(4,2rem)] items-center border-t text-sm'
              >
                <div className='min-w-0 pr-2 py-1.5'>
                  <div className='font-medium'>{itemSlotLabels[row.slot]}</div>
                  {encounterName ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className='text-xs text-muted-foreground truncate cursor-default'>
                          {encounterName}
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>{encounterName}</TooltipContent>
                    </Tooltip>
                  ) : null}
                </div>
                {armorTypeOrder.map((armorType, index) => (
                  <div
                    key={armorType}
                    className={cn(
                      'text-center self-stretch flex items-center justify-center tabular-nums',
                      index % 2 === 0 && 'bg-muted/60',
                      isLastRow && index % 2 === 0 && 'rounded-b',
                    )}
                  >
                    {row.byArmor[armorType] || <span>&nbsp;</span>}
                  </div>
                ))}
              </div>
            )
          })}
        </div>
      </Card>
    </aside>
  )
}

const CompletionOrb = ({
  label,
  count,
  className,
}: {
  label?: string
  count: number
  className: string
}) => {
  return (
    <Badge variant='outline' className='gap-0 px-0 overflow-hidden'>
      <span className='inline-flex items-center gap-1.5 px-2 py-0.5'>
        <span
          className={cn(
            'inline-flex h-2.5 w-2.5 rounded-full border shrink-0',
            className,
          )}
        />
        {label ? <span className='font-medium'>{label}</span> : null}
      </span>
      <span className='self-stretch w-px bg-border' />
      <span className='px-2 py-0.5 text-muted-foreground tabular-nums'>
        {count}
      </span>
    </Badge>
  )
}

export default TierPrioritiesSummary
